import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { draftTimelogsApi } from '@/api/draftTimelogsApi';
import { stopwatchesApi } from '@/api/stopwatchesApi';
import { timelogsApi } from '@/api/timelogsApi';
import { yandexTrackerApi } from '@/api/yandexTrackerApi';
import { Button } from '@/components/Button';
import { useAlertsStore, useStopwatchStore } from '@/configs/store';
import { yaMetrikaStore } from '@/entities/yaMetrika';
import { PublishTargetMenu } from '@/features/draftTimelogs/ui/PublishTargetMenu';
import { LogsMode } from '@/features/logsMode';
import { Stopwatch } from '@/types';
import { AnalyticsEvent } from '@/types/yametrika';
import { formatError } from '@/utils/formatError';

type StopwatchFinishDialogProps = {
  stopwatch: Stopwatch | null;
  open: boolean;
  onClose: (submitted?: boolean) => void;
};

const formatTime = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return [hours, minutes, seconds].map((v) => String(v).padStart(2, '0')).join(':');
};

const secondsToIsoDuration = (totalSeconds: number): string => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);

  if (hours > 0 && minutes > 0) return `PT${hours}H${minutes}M`;

  if (hours > 0) return `PT${hours}H`;

  if (minutes > 0) return `PT${minutes}M`;

  return 'PT1M';
};

export const StopwatchFinishDialog = observer(
  ({ stopwatch, open, onClose }: StopwatchFinishDialogProps) => {
    const store = useStopwatchStore();
    const alertsStore = useAlertsStore();
    const [isCreatingDraft, setIsCreatingDraft] = useState(false);
    const [isPublishing, setIsPublishing] = useState(false);

    if (!stopwatch) return null;

    const canPublish = !!stopwatch.taskKey;
    const duration = secondsToIsoDuration(stopwatch.accumulatedSeconds);

    const handleCreateDraft = async () => {
      setIsCreatingDraft(true);

      try {
        await draftTimelogsApi.create({
          duration,
          start: stopwatch.logAt,
          comment: stopwatch.comment || '',
          taskKey: stopwatch.taskKey,
          taskTitle: stopwatch.taskTitle,
          queueKey: stopwatch.queueKey,
          queueName: stopwatch.queueName,
        });
        await stopwatchesApi.finish(stopwatch.id);
        store.loadStopwatches();
        onClose(true);
      } catch (error) {
        const formatted = formatError(error);
        alertsStore.addAlert({
          title: formatted.title,
          message: formatted.message,
          withReload: false,
          autoClose: true,
        });
      } finally {
        setIsCreatingDraft(false);
      }
    };

    const handlePublish = async (target: LogsMode) => {
      if (!stopwatch.taskKey) return;

      setIsPublishing(true);

      try {
        if (target === LogsMode.v2) {
          await timelogsApi.createTimelogs([
            {
              task: stopwatch.taskKey,
              start: stopwatch.logAt,
              duration,
              comment: stopwatch.comment || '',
            },
          ]);
        } else {
          await yandexTrackerApi.createWorklog({
            taskKey: stopwatch.taskKey,
            duration,
            comment: stopwatch.comment || '',
            startDate: stopwatch.logAt,
          });
        }

        await stopwatchesApi.finish(stopwatch.id);
        store.loadStopwatches();
        yaMetrikaStore.sendEvent(AnalyticsEvent.stopwatchPublish);
        onClose(true);
      } catch (error) {
        const formatted = formatError(error);
        alertsStore.addAlert({
          title: formatted.title,
          message: formatted.message,
          withReload: false,
          autoClose: true,
        });
      } finally {
        setIsPublishing(false);
      }
    };

    const isActing = isCreatingDraft || isPublishing;

    const label = stopwatch.taskKey
      ? `${stopwatch.taskKey} — ${stopwatch.taskTitle ?? ''}`
      : stopwatch.queueKey
        ? `${stopwatch.queueKey} — ${stopwatch.queueName ?? ''}`
        : 'Без задачи';

    return (
      <Dialog
        open={open}
        onClose={() => !isActing && onClose()}
        maxWidth="xs"
        fullWidth
        sx={{ '.MuiPaper-root': { borderRadius: '8px' } }}
      >
        <DialogTitle>Завершить секундомер</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Выберите действие для завершения
          </Typography>

          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              p: 1.5,
            }}
          >
            <Typography
              variant="h5"
              fontFamily="monospace"
              fontWeight="bold"
              sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {formatTime(stopwatch.accumulatedSeconds)}
            </Typography>
            <Typography variant="body2" color="text.secondary" noWrap>
              {label}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {DateTime.fromISO(stopwatch.logAt).toFormat('dd.MM.yyyy, HH:mm')}
            </Typography>
            {stopwatch.comment && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  mt: 0.5,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                }}
              >
                {stopwatch.comment}
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions
          sx={{ flexDirection: 'column', gap: 1, px: 3, pb: 2, alignItems: 'stretch' }}
        >
          <Button
            variant="outlined"
            sx={{ width: '100%' }}
            onClick={handleCreateDraft}
            disabled={isActing}
          >
            {isCreatingDraft ? 'Создание...' : 'Создать черновик'}
          </Button>
          <PublishTargetMenu onPublish={handlePublish}>
            {(open) => (
              <Button
                style={{ marginLeft: 0 }}
                sx={{ width: '100%' }}
                onClick={open}
                disabled={isActing || !canPublish}
              >
                {isPublishing ? 'Публикация...' : 'Опубликовать лог'}
              </Button>
            )}
          </PublishTargetMenu>
          {!canPublish && (
            <Typography variant="caption" color="text.secondary" textAlign="center">
              Для публикации необходимо указать задачу
            </Typography>
          )}
        </DialogActions>
      </Dialog>
    );
  },
);
